import React, { useMemo } from 'react';
import { useDataset } from '../../hooks/useDataset.js';
import { useSelector } from 'react-redux';
import ChartContent from '../base/ChartContent.jsx';
import { useChartWidget } from '../../hooks/useChartWidget.js';
import { useBaseWidget } from '../../hooks/useBaseWidget.jsx';

function CumulativeLineDatasetWidget({ widgetId, widgetRef }) {
    const { formatNumber, numberWithUnit } = useBaseWidget();
    const { tooltipTextFormatter } = useChartWidget();
    const start_date = useSelector(state => state.dashboard.widgets[widgetId].settings?.start_date);
    const end_date = useSelector(state => state.dashboard.widgets[widgetId].settings?.end_date);
    const dataset_mode = useSelector(state => state.dashboard.widgets[widgetId].settings?.dataset_mode) || "single";
    const datasets = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasets);
    const legend_text_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.legend_text_color);
    const colorScheme = useSelector(state => state.dashboard.colorScheme)
    const datasetDataParams = useMemo(() => ({cumulative: true, formatted_date: true, start_date, end_date}), [start_date, end_date]);
    const {data, getNumberUnit, getNumberPrecision, getCommonNumberUnit} = useDataset({ widgetId, datasetDataParams});

    const getChartOptions = ({ interval }) => {
        return {
            maintainAspectRatio: false,
            devicePixelRatio: 2,
            plugins: {
                tooltip: {
                    callbacks: {
                        title: (tips) => {
                            return tooltipTextFormatter(tips[0].raw.label);
                        },
                        label: (tooltipItem) => {
                            const datasetIndex = dataset_mode.match("grouped") ? 0 : tooltipItem.datasetIndex;
                            return formatNumber(tooltipItem.parsed?.y, getNumberUnit(datasetIndex), getNumberPrecision(datasetIndex));
                        },
                    },
                },
                legend: {
                    labels: {
                        color: legend_text_color
                    }
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    suggestedMax: 6,
                    ticks: {
                        callback: (label, index, labels) => formatNumber(label, getCommonNumberUnit(), false),
                    },
                },
                x: {
                    type: "time",
                    time: {
                        minUnit: "day",
                        unit: interval,
                    },
                    ticks: {
                        autoSkip: true,
                        autoSkipPadding: 15,
                    },
                },
            },
        };
    }

    const formatData = () => {
        let currentData = {};
        let currentDatasets = [];

        const isGrouped = dataset_mode.match("grouped");
        let notConfigured = datasets.length == 0;

        datasets.forEach((dataset, i) => {
        if (isGrouped && i > 0) return;

        // If any of the variables is missing variable_id or phase_id, report is not configured.
        notConfigured = notConfigured || !dataset.dataset_id;

        if (notConfigured) return;

        if (isGrouped) {
            currentDatasets = (data[dataset.dataset_id] || []).map((ds, dsIdx) => ({
                label: ds.label,
                data: ds.data.map((v) => ({
                    x: v.value_date,
                    y: numberWithUnit(v.value, dataset.number_unit),
                    label: v.formatted_date,
                })),
                fill: false,
                backgroundColor: colorScheme[dsIdx % colorScheme.length],
                borderColor: colorScheme[dsIdx % colorScheme.length],
                pointRadius: 4,
                lineTension: 0.2,
            }));
        } else {
            currentDatasets.push({
                label: dataset.name_item,
                data: (((data[dataset.dataset_id] || [])[0] || {}).data || []).map((v) => ({
                    x: v.value_date,
                    y: numberWithUnit(v.value, dataset.number_unit),
                    label: v.formatted_date,
                })),
                fill: false,
                backgroundColor: dataset.background_color,
                borderColor: dataset.background_color,
                pointRadius: 4,
                lineTension: 0.2,
            });
        }
        });

        currentData.datasets = currentDatasets;
        currentData.notConfigured = notConfigured;

        return currentData;
    };

    return (
        <ChartContent
            chart_type='line'
            {...{formatData, getChartOptions, data, widgetId, widgetRef}}
        />
    )
}

export default CumulativeLineDatasetWidget;
